/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon, ChevronRightIcon } from '@/shared/ui/radix-icons';
import { VISIT_ACCENT_COLORS, useVisitStatusLabels } from '../constants/visit-colors';
import type { VisitStatus } from '../constants/visit-colors';
import type { KanbanVisit } from '@pages/authenticated/agency-pages/visits/useVisitsKanban';
import { COLUMNS, ColumnCards, CountBadge, pagerBtn } from './VisitsKanbanBoard.shared';
import { Text, UnstyledButton } from '@/shared/ui';

/**
 * Mobile layout C — swipe carousel: full-width snap columns with a visible
 * affordance (prev/next chevrons + pager dots) so navigation is discoverable.
 */
export const MobileCarouselLayout: React.FC<{
  cardsByCol: (col: VisitStatus) => KanbanVisit[];
  selectedId: string | null;
  onSelectVisit: (id: string | null) => void;
}> = ({ cardsByCol, selectedId, onSelectVisit }) => {
  const { t } = useTranslation('features/visits');
  const statusLabels = useVisitStatusLabels();
  const trackRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(0);

  const scrollToPage = (idx: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(COLUMNS.length - 1, idx));
    const child = track.children[clamped] as HTMLElement | undefined;
    if (child) track.scrollTo({ left: child.offsetLeft - track.offsetLeft, behavior: 'smooth' });
  };

  const onScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const idx = Math.round(track.scrollLeft / track.clientWidth);
    setPage(Math.max(0, Math.min(COLUMNS.length - 1, idx)));
  };

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    track.addEventListener('scroll', onScroll, { passive: true });
    return () => track.removeEventListener('scroll', onScroll);
  }, []);

  const accent = VISIT_ACCENT_COLORS[COLUMNS[page]];

  return (
    <div data-testid="kanban-mobile-carousel" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      {/* Pager header: prev/next + current column name */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <UnstyledButton
          type="button"
          aria-label={t('kanban.previous')}
          onClick={() => scrollToPage(page - 1)}
          disabled={page === 0}
          style={{ ...pagerBtn, opacity: page === 0 ? 0.35 : 1 }}
        >
          <ChevronLeftIcon width={18} height={18} />
        </UnstyledButton>
        <Text as="span" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--text-body-sm)', fontWeight: 'var(--font-bold)' as unknown as number, color: 'var(--text-primary)' }}>
          <Text as="span" style={{ width: 8, height: 8, borderRadius: '50%', background: accent }} />
          {statusLabels[COLUMNS[page]]}
          <CountBadge accent={accent} count={cardsByCol(COLUMNS[page]).length} />
        </Text>
        <UnstyledButton
          type="button"
          aria-label={t('kanban.next')}
          onClick={() => scrollToPage(page + 1)}
          disabled={page === COLUMNS.length - 1}
          style={{ ...pagerBtn, opacity: page === COLUMNS.length - 1 ? 0.35 : 1 }}
        >
          <ChevronRightIcon width={18} height={18} />
        </UnstyledButton>
      </div>

      <div
        ref={trackRef}
        style={{
          display: 'flex', gap: 'var(--space-3)', overflowX: 'auto', scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none',
        }}
      >
        {COLUMNS.map((col) => {
          const colAccent = VISIT_ACCENT_COLORS[col];
          const cards = cardsByCol(col);
          return (
            <div
              key={col}
              data-testid="kanban-column"
              style={{
                flex: '0 0 100%', minWidth: '100%', scrollSnapAlign: 'start',
                background: 'var(--surface-primary)', borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--border-subtle)', borderTop: `3px solid ${colAccent}`,
                boxShadow: 'var(--shadow-sm)', overflow: 'hidden',
              }}
            >
              <div style={{ padding: 'var(--space-2)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', minHeight: 120 }}>
                <ColumnCards cards={cards} selectedId={selectedId} onSelectVisit={onSelectVisit} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Pager dots */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 'var(--space-2)', paddingTop: 'var(--space-1)' }}>
        {COLUMNS.map((col, idx) => (
          <UnstyledButton
            key={col}
            type="button"
            aria-label={statusLabels[col]}
            onClick={() => scrollToPage(idx)}
            style={{
              width: idx === page ? 20 : 8, height: 8, borderRadius: 999, border: 'none', cursor: 'pointer',
              padding: 0, transition: 'width 0.2s, background 0.2s',
              background: idx === page ? VISIT_ACCENT_COLORS[col] : 'var(--border-subtle)',
            }}
          >
            <></>
          </UnstyledButton>
        ))}
      </div>
    </div>
  );
};
